import { ReactNode, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import {
  AccountBalance,
  Category,
  Flight,
  ReceiptLong,
  Save,
  VerifiedUser,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { updateEmployeeFees, selectProcesses } from "../processSlices";
import {
  CocVariant,
  Employee,
  EmployeeFees,
  PhotoTypes,
  UpdateEmployeeFeesInput,
} from "../types/processTypes";
import Toast from "../../../components/Layout/Toast";
import { EmployeeMediaCard } from "./EmployeeMediaCard";

interface Props {
  currentEmployee?: Employee;
  handleDialogClose: Function;
}

type FeeSectionKey = keyof EmployeeFees;

type FeeFormValue = {
  amount: string;
  referenceNo: string;
};

const FEE_SECTIONS: {
  key: FeeSectionKey;
  labelKey: string;
  photoType: PhotoTypes;
  icon: ReactNode;
}[] = [
  {
    key: "coc",
    labelKey: "fee_coc",
    photoType: PhotoTypes.FeeReceiptCoc,
    icon: <VerifiedUser fontSize="small" />,
  },
  {
    key: "embassy",
    labelKey: "fee_embassy",
    photoType: PhotoTypes.FeeReceiptEmbassy,
    icon: <AccountBalance fontSize="small" />,
  },
  {
    key: "flight",
    labelKey: "fee_flight",
    photoType: PhotoTypes.FeeReceiptFlight,
    icon: <Flight fontSize="small" />,
  },
  {
    key: "other",
    labelKey: "fee_other",
    photoType: PhotoTypes.FeeReceiptOther,
    icon: <Category fontSize="small" />,
  },
];

export const EmployeeFeesDialog = ({
  currentEmployee,
  handleDialogClose,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { selectedEmployee, error: saveFailed } =
    useAppSelector(selectProcesses);

  const employee = selectedEmployee ?? currentEmployee;

  const seedSection = (key: FeeSectionKey): FeeFormValue => ({
    amount:
      employee?.fees?.[key]?.amount != null
        ? String(employee.fees[key]?.amount)
        : "",
    referenceNo: employee?.fees?.[key]?.referenceNo ?? "",
  });

  const [values, setValues] = useState<Record<FeeSectionKey, FeeFormValue>>({
    coc: seedSection("coc"),
    embassy: seedSection("embassy"),
    flight: seedSection("flight"),
    other: seedSection("other"),
  });
  const [cocVariant, setCocVariant] = useState<CocVariant>(
    employee?.fees?.coc?.variant ?? CocVariant.coc,
  );
  const [dirty, setDirty] = useState<Partial<Record<FeeSectionKey, boolean>>>(
    {},
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  const setSectionValue = (
    key: FeeSectionKey,
    patch: Partial<FeeFormValue>,
  ) => {
    setValues((v) => ({ ...v, [key]: { ...v[key], ...patch } }));
    setDirty((d) => ({ ...d, [key]: true }));
  };

  const handleVariantChange = (variant: CocVariant | null) => {
    if (!variant) return;
    setCocVariant(variant);
    setDirty((d) => ({ ...d, coc: true }));
  };

  const handleSave = async () => {
    if (!employee?._id) return;
    setValidationError(null);

    const input: UpdateEmployeeFeesInput = { _id: employee._id };
    for (const section of FEE_SECTIONS) {
      if (!dirty[section.key]) continue;
      const isCocPlus =
        section.key === "coc" && cocVariant === CocVariant.coc_plus;
      if (isCocPlus) {
        input.coc = { variant: CocVariant.coc_plus };
        continue;
      }
      const { amount, referenceNo } = values[section.key];
      const parsedAmount = Number(amount);
      if (!amount.trim() || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
        setValidationError(
          `${te(section.labelKey)}: ${te("fee_amount_required")}`,
        );
        return;
      }
      if (!referenceNo.trim()) {
        setValidationError(
          `${te(section.labelKey)}: ${te("fee_reference_required")}`,
        );
        return;
      }
      input[section.key] = {
        amount: parsedAmount,
        referenceNo: referenceNo.trim(),
        ...(section.key === "coc" ? { variant: cocVariant } : {}),
      };
    }

    const result = await dispatch(updateEmployeeFees(input));
    if (updateEmployeeFees.fulfilled.match(result)) {
      handleDialogClose(true);
    }
  };

  return (
    <>
      <DialogContent
        sx={{
          width: "100%",
          boxSizing: "border-box",
          pt: 2,
          pb: 2,
          overflowY: "auto",
        }}
      >
        <Grid container spacing={2} alignItems="stretch">
          {FEE_SECTIONS.map((section) => {
            const isCoc = section.key === "coc";
            const isCocPlus = isCoc && cocVariant === CocVariant.coc_plus;
            return (
              <Grid item xs={12} sm={6} md={4} key={section.key}>
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                    bgcolor: "background.paper",
                    transition: "box-shadow 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 6px 18px rgba(25,118,210,0.12)",
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: "action.hover",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        "& svg": { fontSize: "1rem" },
                      }}
                    >
                      {section.icon}
                    </Box>
                    <Typography variant="subtitle2" fontWeight={700} noWrap>
                      {te(section.labelKey)}
                    </Typography>
                  </Stack>
                  <Stack spacing={1.5} sx={{ p: 1.5, flexGrow: 1 }}>
                    {isCoc && (
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        fullWidth
                        color="secondary"
                        value={cocVariant}
                        onChange={(_, variant) => handleVariantChange(variant)}
                      >
                        <ToggleButton value={CocVariant.coc}>
                          {te("coc")}
                        </ToggleButton>
                        <ToggleButton value={CocVariant.coc_plus}>
                          {te("coc_plus")}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                    {isCocPlus ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ my: "auto", textAlign: "center" }}
                      >
                        {te("coc_plus_no_fee")}
                      </Typography>
                    ) : (
                      <>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: "any" }}
                          label={te("fee_amount")}
                          value={values[section.key].amount}
                          onChange={(e) =>
                            setSectionValue(section.key, {
                              amount: e.target.value,
                            })
                          }
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label={te("fee_reference_no")}
                          value={values[section.key].referenceNo}
                          onChange={(e) =>
                            setSectionValue(section.key, {
                              referenceNo: e.target.value,
                            })
                          }
                        />
                        <Box sx={{ maxWidth: 130, mx: "auto", width: "100%" }}>
                          <EmployeeMediaCard
                            title={te("fee_receipt")}
                            src={employee?.fees?.[section.key]?.receiptScan}
                            icon={<ReceiptLong fontSize="small" />}
                            compact
                            uploadInputIdPrefix="employee-fee-receipt"
                            attachmentManage={
                              employee?._id
                                ? {
                                    employeeId: employee._id,
                                    photoType: section.photoType,
                                    enabled: true,
                                    accept: "image/*,.pdf,application/pdf",
                                    suppressGlobalSuccess: true,
                                  }
                                : undefined
                            }
                          />
                        </Box>
                      </>
                    )}
                  </Stack>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
      <Stack
        spacing={1.5}
        sx={{
          px: 3,
          py: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        {validationError && (
          <Box sx={{ width: "100%" }}>
            <Toast severity="error">{validationError}</Toast>
          </Box>
        )}
        {saveFailed && (
          <Box sx={{ width: "100%" }}>
            <Toast severity="error">{saveFailed.message}</Toast>
          </Box>
        )}
        <Button
          fullWidth
          size="large"
          variant="contained"
          color="secondary"
          startIcon={<Save />}
          onClick={handleSave}
          sx={{ borderRadius: 2, py: 1.25, fontWeight: 600 }}
        >
          {t("save")}
        </Button>
      </Stack>
    </>
  );
};
